import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { useGetUserProfileQuery } from "../../../../../api/userAuthApi";
import { useProfileAddressUpdate } from "../../../../../hooks/useProfileAddressUpdate";

import {
  createShippingSchema,
  ShippingFormData,
} from "../../../../../../ecommerce/checkout/helpers/checkoutValidation";

import PersonalInfoFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/PersonalInfoFields";
import AddressFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/AddressFields";
import ContactInfoFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/ContactInfoFields";

const ProfileShippingForm: React.FC = () => {
  const { t } = useTranslation();

  const { data: profile, isLoading: isProfileLoading } =
    useGetUserProfileQuery();

  const { handleUpdate, isUpdating } = useProfileAddressUpdate("shipping");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<ShippingFormData>({
    resolver: zodResolver(createShippingSchema(t)),
    defaultValues: {
      country: "", // No hardcoded default
    },
  });

  useEffect(() => {
    if (profile?.shippingAddress) {
      // Logic: If there is only 1 allowed country, pre-select it if the user profile has no country saved.
      const allowedCountries =
        window.checkoutFlowVariables?.allowedCountries || {};
      const countryCodes = Object.keys(allowedCountries);
      const singleCountryCode =
        countryCodes.length === 1 ? countryCodes[0] : "";
      const countryToSet =
        profile.shippingAddress.country || singleCountryCode || "";

      reset({
        ...profile.shippingAddress,
        country: countryToSet,
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: ShippingFormData) => {
    const shippingPayload = {
      first_name: data.firstName,
      last_name: data.lastName,
      company: data.company,
      address_1: data.address1,
      address_2: data.address2,
      city: data.city,
      state: data.state,
      postcode: data.postcode,
      country: data.country,
      phone: data.phone,
    };

    handleUpdate({
      shipping_address: shippingPayload,
    });
  };

  if (isProfileLoading) {
    return (
      <div className="profile-address-form">
        <Skeleton count={8} height={40} style={{ marginBottom: "1rem" }} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="profile-address-form">
      <h3 className="profile-address-form__title">
        {t("user.addresses.shippingTitle", "Shipping Address")}
      </h3>

      <div className="profile-address-form__content">
        <PersonalInfoFields
          register={register}
          errors={errors}
          watch={watch}
          setValue={setValue}
          variant="shipping"
        />

        <AddressFields
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <ContactInfoFields
          register={register}
          errors={errors}
          showEmail={false}
        />
      </div>

      <div className="profile-address-form__footer">
        <button
          type="submit"
          className="button button--primary profile-address-form__submit-btn"
          disabled={isUpdating || !isDirty}
        >
          {isUpdating
            ? t("user.accountDetails.saving", "Saving...")
            : t("user.accountDetails.saveChanges", "Save Changes")}
        </button>
      </div>
    </form>
  );
};

export default ProfileShippingForm;
